import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { User, Calendar, Edit2, Plus, Trash2, BarChart3, MessageSquare } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface HistoryEntry {
  id: string;
  action: string;
  created_at: string;
  user_id: string;
  user_name: string | null;
  changed_fields?: any;
}

interface EditHistoryDialogProps {
  leadId: string;
  leadName: string;
  createdAt: string;
  creatorId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EditHistoryDialog = ({
  leadId,
  leadName,
  createdAt,
  creatorId,
  open,
  onOpenChange,
}: EditHistoryDialogProps) => {
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [creatorName, setCreatorName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (open) {
      fetchHistory();
    }
  }, [open, leadId, creatorId]);

  // Profiles RLS only exposes your own row, so teammate names must come from
  // the group display-name RPC.
  const buildNameMap = async (userIds: string[]) => {
    const map = new Map<string, string>();
    if (userIds.length === 0) return map;

    const { data: lead } = await supabase
      .from("leads")
      .select("group_id")
      .eq("id", leadId)
      .maybeSingle();

    if (lead?.group_id) {
      const { data: members } = await supabase.rpc("get_group_member_display_names", {
        p_group_id: lead.group_id,
      });
      (members ?? []).forEach((m: any) => {
        if (m?.user_id && m?.display_name) map.set(m.user_id, m.display_name);
      });
    }

    // Own profile (covers personal workspace leads and self-edits)
    const { data: { user } } = await supabase.auth.getUser();
    if (user && userIds.includes(user.id) && !map.has(user.id)) {
      const { data: me } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("user_id", user.id)
        .maybeSingle();
      map.set(user.id, me?.full_name || user.email || "You");
    }

    return map;
  };

  const fetchHistory = async () => {
    setIsLoading(true);
    try {
      const { data: historyData } = await supabase
        .from("lead_history")
        .select("id, action, created_at, user_id, changed_fields")
        .eq("lead_id", leadId)
        .order("created_at", { ascending: false });

      const rows = historyData ?? [];
      const userIds = [...new Set([...rows.map(h => h.user_id), ...(creatorId ? [creatorId] : [])])].filter(Boolean) as string[];
      const nameMap = await buildNameMap(userIds);

      if (creatorId) setCreatorName(nameMap.get(creatorId) ?? "Unknown user");

      setHistory(rows.map(entry => ({
        id: entry.id,
        action: entry.action,
        user_id: entry.user_id,
        created_at: entry.created_at,
        user_name: nameMap.get(entry.user_id) ?? "Unknown user",
        changed_fields: entry.changed_fields,
      })));
    } catch (error) {
      console.error("Error fetching edit history:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActionIcon = (action: string) => {
    if (action.startsWith('gantt_')) {
      return <BarChart3 className="h-4 w-4" />;
    }
    if (action === 'updated') {
      return <Edit2 className="h-4 w-4" />;
    }
    if (action === 'created') {
      return <Plus className="h-4 w-4" />;
    }
    return <Edit2 className="h-4 w-4" />;
  };

  const getActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      updated: "Lead Updated",
      created: "Lead Created",
      gantt_task_added: "Task Added",
      gantt_task_updated: "Task Updated",
      gantt_task_deleted: "Task Deleted",
    };
    return labels[action] || action;
  };

  const getActionColor = (action: string) => {
    if (action === 'gantt_task_added' || action === 'created') {
      return "bg-green-500/10 text-green-600";
    }
    if (action === 'gantt_task_deleted') {
      return "bg-red-500/10 text-red-600";
    }
    if (action.startsWith('gantt_')) {
      return "bg-purple-500/10 text-purple-600";
    }
    return "bg-blue-500/10 text-blue-600";
  };

  const getChangedFieldsDescription = (entry: HistoryEntry) => {
    const changedFields = entry.changed_fields;
    if (!changedFields) return null;

    // Handle gantt changes
    if (changedFields.type === 'gantt') {
      if (entry.action === 'gantt_task_added') {
        return `Added task: "${changedFields.task_title}"`;
      }
      if (entry.action === 'gantt_task_deleted') {
        return `Deleted task: "${changedFields.task_title}"`;
      }
      if (entry.action === 'gantt_task_updated') {
        const changes: string[] = [];
        const oldData = changedFields.old;
        const newData = changedFields.new;
        
        if (oldData?.title !== newData?.title) {
          changes.push(`Title: "${oldData?.title}" → "${newData?.title}"`);
        }
        if (oldData?.notes !== newData?.notes) {
          changes.push(`Notes updated`);
        }
        if (oldData?.start_position !== newData?.start_position) {
          changes.push(`Position moved`);
        }
        if (oldData?.width !== newData?.width) {
          changes.push(`Duration changed`);
        }
        if (oldData?.color !== newData?.color) {
          changes.push(`Color changed`);
        }
        
        return changes.length > 0 
          ? `Task "${changedFields.task_title}": ${changes.join(", ")}`
          : `Task "${changedFields.task_title}" modified`;
      }
    }

    // Handle lead field changes
    if (!changedFields.old || !changedFields.new) return null;

    const changes: string[] = [];
    const oldData = changedFields.old;
    const newData = changedFields.new;

    const fieldLabels: Record<string, string> = {
      lead_name: "Name",
      company: "Company",
      email: "Email",
      phone: "Phone",
      status: "Status",
      priority: "Priority",
      value: "Value",
      source: "Source",
      conversation_notes: "Notes",
      next_action: "Next Action",
      next_action_date: "Next Action Date"
    };

    Object.keys(fieldLabels).forEach(field => {
      if (oldData[field] !== newData[field]) {
        const label = fieldLabels[field];
        const oldVal = oldData[field] || "(empty)";
        const newVal = newData[field] || "(empty)";
        
        if (field === "conversation_notes") {
          changes.push(`${label} updated`);
        } else if (field === "value" && newVal !== "(empty)") {
          changes.push(`${label}: $${oldVal} → $${newVal}`);
        } else {
          changes.push(`${label}: ${oldVal} → ${newVal}`);
        }
      }
    });

    return changes.length > 0 ? changes.join(" • ") : null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit2 className="h-5 w-5" />
            Edit History
          </DialogTitle>
          <p className="text-sm text-muted-foreground">{leadName}</p>
        </DialogHeader>

        <ScrollArea className="h-[400px] pr-4">
          <div className="space-y-3">
            {/* Creation entry */}
            <div className="flex gap-3 p-3 rounded-lg bg-secondary/30 border">
              <div className="flex-shrink-0 mt-1">
                <div className="p-1.5 rounded-full bg-green-500/10 text-green-600">
                  <Plus className="h-4 w-4" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <Badge className="bg-green-500/10 text-green-600 text-xs">
                    Lead Created
                  </Badge>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDate(createdAt)}
                  </span>
                </div>
                {creatorName && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <User className="h-3 w-3" />
                    <span>by {creatorName}</span>
                  </div>
                )}
              </div>
            </div>

            {isLoading ? (
              <div className="text-center text-muted-foreground text-sm py-8">
                Loading history...
              </div>
            ) : history.length === 0 ? (
              <div className="text-center text-muted-foreground text-sm py-8">
                No edits recorded yet.
              </div>
            ) : (
              history.map((entry) => {
                const changeDescription = getChangedFieldsDescription(entry);
                return (
                  <div key={entry.id} className="flex gap-3 p-3 rounded-lg bg-secondary/30 border">
                    <div className="flex-shrink-0 mt-1">
                      <div className={`p-1.5 rounded-full ${getActionColor(entry.action)}`}>
                        {getActionIcon(entry.action)}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                        <Badge className={`${getActionColor(entry.action)} text-xs`}>
                          {getActionLabel(entry.action)}
                        </Badge>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(entry.created_at)}
                        </span>
                      </div>
                      {entry.user_name && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
                          <User className="h-3 w-3" />
                          <span>by {entry.user_name}</span>
                        </div>
                      )}
                      {changeDescription && (
                        <p className="text-xs text-muted-foreground bg-background/50 p-2 rounded mt-1">
                          {changeDescription}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default EditHistoryDialog;
